import { router } from '@inertiajs/react'
import { useState } from 'react'
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  PlusIcon,
  CalendarIcon,
  CloudIcon,
  CheckCircleIcon,
  XCircleIcon,
} from 'lucide-react'

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { ExternalEventDetailDialog } from '@/components/external-event-detail-dialog'
import { InternalAppointmentDetailDialog } from '@/components/internal-appointment-detail-dialog'
import { QuickAppointmentDialog } from '@/components/quick-appointment-dialog'
import authService from '@/lib/auth'

interface Appointment {
  id: number | string
  lead_id?: number
  lead_name?: string
  date: string
  time: string
  duration: number
  title?: string
  appointment_type: string
  technician: string
  status: string
  notes: string
  location?: string
  synced_to_google: boolean
  synced_to_team: boolean
  synced_to_personal: boolean
  created_at: string
  is_external?: boolean
}

interface Lead {
  id: number
  name: string
}

interface Connection {
  id: number
  calendar_type: string
  google_account_email: string
  user_name: string | null
  last_synced_at: string | null
  sync_enabled: boolean
  webhook_expires_at: string | null
  business_type: string
}

interface ShopGoogleCalendarIndexProps {
  appointments: Appointment[]
  view_type: string
  current_date: string
  leads: Lead[]
  connections: Connection[]
  business_type: string
}

export default function ShopGoogleCalendarIndex({
  appointments,
  view_type,
  current_date,
  leads,
  connections,
  business_type,
}: ShopGoogleCalendarIndexProps) {
  const [viewType, setViewType] = useState(view_type)
  const [currentDate, setCurrentDate] = useState(new Date(current_date))
  const [dialogOpen, setDialogOpen] = useState(false)
  const [filterType, setFilterType] = useState<'all' | 'team' | 'personal'>('all')
  const [externalEventDialogOpen, setExternalEventDialogOpen] = useState(false)
  const [selectedExternalEvent, setSelectedExternalEvent] = useState<any>(null)
  const [selectedConnection, setSelectedConnection] = useState<Connection | null>(null)
  const [internalAppointmentDialogOpen, setInternalAppointmentDialogOpen] = useState(false)
  const [selectedInternalAppointment, setSelectedInternalAppointment] = useState<Appointment | null>(null)

  // Helper function to format date in local timezone as YYYY-MM-DD
  const formatDateLocal = (date: Date): string => {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
  }

  const handleViewChange = (view: string) => {
    setViewType(view)
    router.get(
      '/shop/google_calendar',
      {
        view,
        date: formatDateLocal(currentDate),
      },
      {
        preserveState: true,
        preserveScroll: true,
      }
    )
  }

  const handleDateChange = (direction: 'prev' | 'next') => {
    let newDate = new Date(currentDate)

    if (viewType === 'month') {
      newDate.setMonth(newDate.getMonth() + (direction === 'next' ? 1 : -1))
    } else if (viewType === 'week') {
      newDate.setDate(newDate.getDate() + (direction === 'next' ? 7 : -7))
    } else {
      newDate.setDate(newDate.getDate() + (direction === 'next' ? 1 : -1))
    }

    setCurrentDate(newDate)
    router.get(
      '/shop/google_calendar',
      {
        view: viewType,
        date: formatDateLocal(newDate),
      },
      {
        preserveState: true,
        preserveScroll: true,
      }
    )
  }

  const handleToday = () => {
    const today = new Date()
    setCurrentDate(today)
    router.get(
      '/shop/google_calendar',
      {
        view: viewType,
        date: formatDateLocal(today),
      },
      {
        preserveState: true,
        preserveScroll: true,
      }
    )
  }

  const handleConnectCalendar = async () => {
    try {
      const response = await fetch(`/google_calendar/oauth/authorize?business_type=${business_type}`, {
        headers: {
          ...authService.authHeaders(),
          'Accept': 'application/json',
        },
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await response.json()

      // Open OAuth popup
      const width = 600
      const height = 700
      const left = window.screen.width / 2 - width / 2
      const top = window.screen.height / 2 - height / 2

      const popup = window.open(
        data.oauth_url,
        'google_calendar_oauth',
        `width=${width},height=${height},left=${left},top=${top}`
      )

      // Listen for callback message
      const handleMessage = (event: MessageEvent) => {
        if (event.data.type === 'google_calendar_oauth_callback') {
          window.removeEventListener('message', handleMessage)
          router.reload()
        }
      }

      window.addEventListener('message', handleMessage)
    } catch (error) {
      console.error('Failed to initiate Google Calendar OAuth:', error)
    }
  }

  const handleDisconnectCalendar = () => {
    if (confirm('Are you sure you want to disconnect Google Calendar for Modern Shop?')) {
      router.delete(`/google_calendar/oauth/disconnect?business_type=${business_type}`)
    }
  }

  const handleExternalEventClick = async (eventId: string | number, connection: Connection) => {
    try {
      const response = await fetch(
        `/google_calendar/external_events/${eventId}.json?connection_id=${connection.id}`,
        {
          headers: {
            ...authService.authHeaders(),
            'Accept': 'application/json',
          },
        }
      )

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await response.json()
      setSelectedExternalEvent(data.event)
      setSelectedConnection(data.connection)
      setExternalEventDialogOpen(true)
    } catch (error) {
      console.error('Failed to fetch event details:', error)
    }
  }

  const handleInternalAppointmentClick = async (appointmentId: string | number) => {
    try {
      const response = await fetch(
        `/appointments/${appointmentId}.json`,
        {
          headers: {
            ...authService.authHeaders(),
            'Accept': 'application/json',
          },
        }
      )

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await response.json()
      setSelectedInternalAppointment(data.appointment)
      setInternalAppointmentDialogOpen(true)
    } catch (error) {
      console.error('Failed to fetch appointment details:', error)
    }
  }

  const formatDateHeader = () => {
    if (viewType === 'month') {
      return currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
    } else if (viewType === 'week') {
      const weekStart = new Date(currentDate)
      weekStart.setDate(currentDate.getDate() - currentDate.getDay())
      const weekEnd = new Date(weekStart)
      weekEnd.setDate(weekStart.getDate() + 6)
      return `${weekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${weekEnd.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
    } else {
      return currentDate.toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      })
    }
  }

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear()
    const month = date.getMonth()
    const firstDay = new Date(year, month, 1)
    const lastDay = new Date(year, month + 1, 0)
    const daysInMonth = lastDay.getDate()
    const startingDayOfWeek = firstDay.getDay()

    const days = []

    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null)
    }

    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day, 12, 0, 0))
    }

    return days
  }

  const getAppointmentsForDate = (date: Date | null) => {
    if (!date) return []
    return filteredAppointments.filter((apt) => apt.date === formatDateLocal(date))
  }

  const filteredAppointments = appointments.filter((apt) => {
    if (filterType === 'all') return true
    if (filterType === 'team') return apt.synced_to_team
    if (filterType === 'personal') return apt.synced_to_personal
    return true
  })

const connection = connections[0]

  const renderMonthView = () => {
    const days = getDaysInMonth(currentDate)
    const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

    return (
      <div className="grid grid-cols-7 gap-px bg-muted">
        {weekDays.map((day) => (
          <div key={day} className="bg-background p-2 text-center font-semibold text-sm">
            {day}
          </div>
        ))}

        {days.map((day, index) => {
          const dayAppointments = day ? getAppointmentsForDate(day).sort((a, b) => {
            return parseTimeToMinutes(a.time) - parseTimeToMinutes(b.time)
          }) : []
          const isToday = day && day.toDateString() === new Date().toDateString()

          return (
            <div
              key={index}
              className={`bg-background min-h-24 p-2 ${!day ? 'bg-muted/50' : ''} ${isToday ? 'bg-orange-50 dark:bg-orange-950/20' : ''}`}
            >
              {day && (
                <>
                  <div
                    className={`text-sm font-medium mb-1 ${isToday ? 'text-orange-600 font-bold' : ''}`}
                  >
                    {day.getDate()}
                  </div>
                  <div className="space-y-1">
                    {dayAppointments.map((apt) => (
                      <div
                        key={apt.id}
                        className={`text-xs p-1 rounded cursor-pointer border hover:border-orange-600 ${
                          apt.is_external
                            ? 'bg-purple-100 dark:bg-purple-900/30 border-purple-200 dark:border-purple-800'
                            : 'bg-blue-100 dark:bg-blue-900/30 border-blue-200 dark:border-blue-800'
                        }`}
                        onClick={() => {
                          if (apt.is_external && connection) {
                            handleExternalEventClick(apt.id, connection)
                          } else {
                            handleInternalAppointmentClick(apt.id)
                          }
                        }}
                      >
                        <div className="flex items-center justify-between">
                          <div className="font-medium truncate">{apt.time}</div>
                          {apt.synced_to_google && (
                            <CloudIcon className="h-3 w-3 text-green-600" />
                          )}
                        </div>
                        <div className="truncate text-muted-foreground">
                          {apt.is_external ? apt.appointment_type : (apt.title || apt.appointment_type)}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )
        })}
      </div>
    )
  }

  const renderWeekView = () => {
    const weekStart = new Date(currentDate)
    weekStart.setDate(currentDate.getDate() - currentDate.getDay())

    const weekDays = []
    for (let i = 0; i < 7; i++) {
      const day = new Date(weekStart)
      day.setDate(weekStart.getDate() + i)
      weekDays.push(day)
    }

    return (
      <div className="grid grid-cols-7 gap-px bg-muted">
        {weekDays.map((day) => {
          const dayAppointments = getAppointmentsForDate(day).sort((a, b) => {
            return parseTimeToMinutes(a.time) - parseTimeToMinutes(b.time)
          })
          const isToday = day.toDateString() === new Date().toDateString()

          return (
            <div
              key={day.toISOString()}
              className={`bg-background p-2 ${isToday ? 'bg-orange-50 dark:bg-orange-950/20' : ''}`}
            >
              <div className={`text-sm font-semibold mb-2 text-center ${isToday ? 'text-orange-600' : ''}`}>
                {day.toLocaleDateString('en-US', { weekday: 'short' })} {day.getDate()}
              </div>
              <div className="space-y-1">
                {dayAppointments.map((apt) => (
                  <div
                    key={apt.id}
                    className={`text-xs p-1 rounded cursor-pointer border hover:border-orange-600 ${
                      apt.is_external
                        ? 'bg-purple-100 dark:bg-purple-900/30 border-purple-200 dark:border-purple-800'
                        : 'bg-blue-100 dark:bg-blue-900/30 border-blue-200 dark:border-blue-800'
                    }`}
                    onClick={() => {
                      if (apt.is_external && connection) {
                        handleExternalEventClick(apt.id, connection)
                      } else {
                        handleInternalAppointmentClick(apt.id)
                      }
                    }}
                  >
                    <div className="flex items-center justify-between">
                      <div className="font-medium truncate">{apt.time}</div>
                      {apt.synced_to_google && (
                        <CloudIcon className="h-3 w-3 text-green-600" />
                      )}
                    </div>
                    <div className="truncate text-muted-foreground">
                      {apt.is_external ? apt.appointment_type : (apt.title || apt.appointment_type)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )
        })}
      </div>
    )
  }

  // Helper function to convert time string to comparable number (minutes since midnight)
  const parseTimeToMinutes = (timeStr: string): number => {
    // Handle "14:30" format (24-hour)
    const timeMatch = timeStr.match(/(\d{1,2}):(\d{2})/)

    if (timeMatch) {
      const [, hours, minutes] = timeMatch
      return parseInt(hours) * 60 + parseInt(minutes)
    }

    return 0 // Fallback for invalid format
  }

  const renderDayView = () => {
    const dayAppointments = getAppointmentsForDate(currentDate).sort((a, b) => {
      return parseTimeToMinutes(a.time) - parseTimeToMinutes(b.time)
    })
    const isToday = currentDate.toDateString() === new Date().toDateString()

    return (
      <div className="space-y-2">
        <div className={`text-lg font-semibold mb-4 ${isToday ? 'text-orange-600' : ''}`}>
          {currentDate.toLocaleDateString('en-US', {
            weekday: 'long',
            month: 'long',
            day: 'numeric',
            year: 'numeric'
          })}
        </div>
        {dayAppointments.length > 0 ? (
          <div className="space-y-2">
            {dayAppointments.map((apt) => (
              <div
                key={apt.id}
                className={`p-4 rounded-lg cursor-pointer border hover:border-orange-600 ${
                  apt.is_external
                    ? 'bg-purple-100 dark:bg-purple-900/30 border-purple-200 dark:border-purple-800'
                    : 'bg-blue-100 dark:bg-blue-900/30 border-blue-200 dark:border-blue-800'
                }`}
                onClick={() => {
                  if (apt.is_external && connection) {
                    handleExternalEventClick(apt.id, connection)
                  } else {
                    handleInternalAppointmentClick(apt.id)
                  }
                }}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="font-semibold text-base">{apt.time}</div>
                  {apt.synced_to_google && (
                    <CloudIcon className="h-4 w-4 text-green-600" />
                  )}
                </div>
                <div className="text-sm font-medium">
                  {apt.is_external ? apt.appointment_type : (apt.title || apt.appointment_type)}
                </div>
                {apt.location && (
                  <div className="text-xs text-muted-foreground mt-1">
                    {apt.location}
                  </div>
                )}
                {apt.notes && (
                  <div className="text-xs text-muted-foreground mt-1">
                    {apt.notes}
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            No appointments scheduled for this day
          </div>
        )}
      </div>
    )
  }

  const renderCalendarView = () => {
    switch (viewType) {
      case 'week':
        return renderWeekView()
      case 'day':
        return renderDayView()
      default:
        return renderMonthView()
    }
  }

  return (
    <div className="flex flex-1 flex-col">
      <div className="@container/main flex flex-1 flex-col gap-2">
        <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6">
          <div className="px-4 lg:px-6">
            <div className="flex flex-col gap-4">
              {/* Header */}
              <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h1 className="text-2xl font-bold tracking-tight">Google Calendar - Modern Shop</h1>
                  <p className="text-sm text-muted-foreground">
                    Synced appointments for shop operations
                  </p>
                </div>
                <Button
                  onClick={() => setDialogOpen(true)}
                  className="shrink-0"
                  data-testid="add-appointment-button"
                >
                  <PlusIcon className="h-4 w-4 mr-2" />
                  Add Appointment
                </Button>
              </div>

              {/* Appointment Insights */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm font-medium">Appointment Insights</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid gap-4 md:grid-cols-3">
                    <div>
                      <div className="text-2xl font-bold">{appointments.length}</div>
                      <div className="text-xs text-muted-foreground">Total Appointments</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold">
                        {appointments.filter((a) => a.synced_to_google).length}
                      </div>
                      <div className="text-xs text-muted-foreground">Synced to Google</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold">
                        {appointments.filter((a) => !a.synced_to_google).length}
                      </div>
                      <div className="text-xs text-muted-foreground">Not Synced</div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Calendar Controls */}
              <Card>
                <CardHeader>
                  <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex items-center gap-2">
                      <Button variant="outline" size="sm" onClick={() => handleDateChange('prev')}>
                        <ChevronLeftIcon className="h-4 w-4" />
                      </Button>
                      <Button variant="outline" size="sm" onClick={handleToday}>
                        Today
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => handleDateChange('next')}>
                        <ChevronRightIcon className="h-4 w-4" />
                      </Button>
                      <h2 className="ml-4 text-lg font-semibold">{formatDateHeader()}</h2>
                    </div>

                    <div className="flex items-center gap-4">
                      {/* View Tabs */}
                      <Tabs value={viewType} onValueChange={handleViewChange}>
                        <TabsList>
                          <TabsTrigger value="month">Month</TabsTrigger>
                          <TabsTrigger value="week">Week</TabsTrigger>
                          <TabsTrigger value="day">Day</TabsTrigger>
                        </TabsList>
                      </Tabs>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>{renderCalendarView()}</CardContent>
              </Card>

              {/* Connection Status Card */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm font-medium">Calendar Connection</CardTitle>
                </CardHeader>
                <CardContent>
                  {connection ? (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <CheckCircleIcon className="h-4 w-4 text-green-600" />
                          <span className="text-sm font-medium">Connected</span>
                          <Badge variant="outline">Shop</Badge>
                        </div>
                        <span className={`text-xs ${connection.sync_enabled ? 'text-green-600' : 'text-orange-600'}`}>
                          Sync: {connection.sync_enabled ? 'Enabled' : 'Disabled'}
                        </span>
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {connection.google_account_email}
                      </div>
                      <div className="flex flex-col gap-1 text-xs text-muted-foreground">
                        {connection.last_synced_at && (
                          <div>
                            Last synced: {new Date(connection.last_synced_at).toLocaleString()}
                          </div>
                        )}
                        {connection.webhook_expires_at && (
                          <div>
                            Webhook expires: {new Date(connection.webhook_expires_at).toLocaleDateString()}
                          </div>
                        )}
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleDisconnectCalendar}
                      >
                        Disconnect
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <XCircleIcon className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm text-muted-foreground">Not connected</span>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleConnectCalendar}
                      >
                        Connect Google Calendar
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>

      <ExternalEventDetailDialog
        open={externalEventDialogOpen}
        onOpenChange={setExternalEventDialogOpen}
        event={selectedExternalEvent}
        connection={selectedConnection}
      />

      <InternalAppointmentDetailDialog
        open={internalAppointmentDialogOpen}
        onOpenChange={setInternalAppointmentDialogOpen}
        appointment={selectedInternalAppointment}
      />

      <QuickAppointmentDialog open={dialogOpen} onOpenChange={setDialogOpen} leads={leads} businessType="shop" />
    </div>
  )
}
